// src/components/admin/BulkUserImport.js - CSV bulk user import: upload -> preview -> confirm -> results
import { useState } from 'react';
import PropTypes from 'prop-types';
import { useAuth } from '../../contexts/AuthContext';
import { useApiCall } from '../../hooks/useApiCall';
import apiService from '../../services/apiService';
import './BulkUserImport.css';

const CSV_TEMPLATE = 'email,user name,cohort name\njane@example.com,Jane Doe,Q3 Cohort\n';

const downloadCsvTemplate = () => {
  const blob = new Blob([CSV_TEMPLATE], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'user-import-template.csv';
  a.click();
  URL.revokeObjectURL(url);
};

const BulkUserImportModal = ({ tenantId, onClose, onComplete }) => {
  const { user } = useAuth();
  const { loading, error, makeRequest, clearError } = useApiCall();

  const [step, setStep] = useState('upload'); // 'upload' | 'preview' | 'results'
  const [file, setFile] = useState(null);
  const [previewData, setPreviewData] = useState(null);
  const [selectedRowNumbers, setSelectedRowNumbers] = useState(new Set());
  const [resultsData, setResultsData] = useState(null);

  const handleFileSelected = (e) => {
    clearError();
    setFile(e.target.files[0] || null);
  };

  const handlePreview = async () => {
    if (!file) return;
    try {
      const formData = new FormData();
      formData.append('csvFile', file);

      const data = await makeRequest(
        () => apiService.postFormData(`/api/v1/tenants/${tenantId}/users/import/preview`, formData, user),
        'Analyzing CSV...'
      );

      if (data.success) {
        setPreviewData(data.data);
        // Default: include every row that isn't a hard error.
        const includable = data.data.rows.filter(r => r.status !== 'error').map(r => r.rowNumber);
        setSelectedRowNumbers(new Set(includable));
        setStep('preview');
      }
    } catch (err) {
      console.error('Error previewing CSV import:', err);
    }
  };

  const toggleRow = (rowNumber, checked) => {
    setSelectedRowNumbers(prev => {
      const next = new Set(prev);
      checked ? next.add(rowNumber) : next.delete(rowNumber);
      return next;
    });
  };

  const toggleAll = (checked) => {
    if (!previewData) return;
    if (checked) {
      setSelectedRowNumbers(new Set(previewData.rows.filter(r => r.status !== 'error').map(r => r.rowNumber)));
    } else {
      setSelectedRowNumbers(new Set());
    }
  };

  const handleConfirm = async () => {
    if (!previewData) return;
    const rows = previewData.rows
      .filter(r => selectedRowNumbers.has(r.rowNumber))
      .map(r => ({ rowNumber: r.rowNumber, email: r.email, displayName: r.displayName, cohortName: r.cohortName }));

    if (rows.length === 0) return;

    try {
      const data = await makeRequest(
        () => apiService.post(`/api/v1/tenants/${tenantId}/users/import/confirm`, { rows }, user),
        'Creating users...'
      );

      if (data.success) {
        setResultsData(data.data);
        setStep('results');
      }
    } catch (err) {
      console.error('Error confirming CSV import:', err);
    }
  };

  const handleDone = () => {
    onComplete();
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content bulk-import-modal">
        <div className="modal-header">
          <h3 className="modal-title">Import Users from CSV</h3>
          <button onClick={onClose} className="modal-close" disabled={loading}>
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {error && (
          <div className="alert alert-error mb-4">
            <div><strong>Error:</strong> {error}</div>
          </div>
        )}

        {step === 'upload' && (
          <UploadStep
            file={file}
            loading={loading}
            onFileSelected={handleFileSelected}
            onDownloadTemplate={downloadCsvTemplate}
            onCancel={onClose}
            onPreview={handlePreview}
          />
        )}

        {step === 'preview' && previewData && (
          <PreviewStep
            previewData={previewData}
            selectedRowNumbers={selectedRowNumbers}
            loading={loading}
            onToggleRow={toggleRow}
            onToggleAll={toggleAll}
            onCancel={onClose}
            onConfirm={handleConfirm}
          />
        )}

        {step === 'results' && resultsData && (
          <ResultsStep resultsData={resultsData} onDone={handleDone} />
        )}
      </div>
    </div>
  );
};

const UploadStep = ({ file, loading, onFileSelected, onDownloadTemplate, onCancel, onPreview }) => (
  <div>
    <p className="text-sm text-gray-600 mb-4">
      Upload a CSV with columns <code>email</code> (required), <code>user name</code> (optional), and{' '}
      <code>cohort name</code> (optional). Each row creates one invited user.
    </p>

    <div className="mb-4">
      <button type="button" className="btn btn-secondary btn-sm" onClick={onDownloadTemplate}>
        Download CSV template
      </button>
    </div>

    <div className="mb-6">
      <label className="form-label" htmlFor="csvFile">CSV File</label>
      <input
        type="file"
        id="csvFile"
        accept=".csv,text/csv"
        onChange={onFileSelected}
        className="form-input"
        disabled={loading}
      />
    </div>

    <div className="modal-footer">
      <button type="button" onClick={onCancel} className="btn btn-secondary flex-1" disabled={loading}>
        Cancel
      </button>
      <button
        type="button"
        onClick={onPreview}
        className="btn btn-primary flex-1"
        disabled={loading || !file}
      >
        {loading ? 'Analyzing...' : 'Preview Import'}
      </button>
    </div>
  </div>
);

const STATUS_LABELS = {
  valid: 'Ready',
  warning: 'No cohort',
  skipped: 'Skipped',
  error: 'Error'
};

const PreviewStep = ({ previewData, selectedRowNumbers, loading, onToggleRow, onToggleAll, onCancel, onConfirm }) => {
  const { summary, rows, cohortWarnings } = previewData;
  const includableRows = rows.filter(r => r.status !== 'error');
  const allIncludableSelected = includableRows.length > 0 && includableRows.every(r => selectedRowNumbers.has(r.rowNumber));

  return (
    <div>
      <div className="import-summary-strip mb-4">
        <span className="import-summary-item valid">{summary.validCount} ready</span>
        <span className="import-summary-item warning">{summary.createdWithoutCohortCount} without cohort</span>
        <span className="import-summary-item skipped">{summary.skippedExistingCount + summary.skippedDuplicateInFileCount} skipped</span>
        <span className="import-summary-item error">{summary.errorCount} errors</span>
      </div>

      {cohortWarnings.length > 0 && (
        <div className="import-cohort-warnings mb-4">
          {cohortWarnings.map(w => (
            <div key={w.cohortName} className="alert alert-warning">
              Cohort <strong>&apos;{w.cohortName}&apos;</strong> doesn&apos;t exist — {w.affectedRowCount} user
              {w.affectedRowCount === 1 ? '' : 's'} will be created without a cohort. Continue, or cancel and fix
              the file first.
            </div>
          ))}
        </div>
      )}

      <div className="import-preview-table-wrapper mb-4">
        <table className="import-preview-table">
          <thead>
            <tr>
              <th>
                <input type="checkbox" checked={allIncludableSelected} onChange={(e) => onToggleAll(e.target.checked)} />
              </th>
              <th>Row</th>
              <th>Email</th>
              <th>Name</th>
              <th>Cohort</th>
              <th>Status</th>
              <th>Note</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(row => (
              <tr key={row.rowNumber} className={`import-row-${row.status}`}>
                <td>
                  <input
                    type="checkbox"
                    checked={selectedRowNumbers.has(row.rowNumber)}
                    onChange={(e) => onToggleRow(row.rowNumber, e.target.checked)}
                    disabled={row.status === 'error'}
                  />
                </td>
                <td>{row.rowNumber}</td>
                <td>{row.email}</td>
                <td>{row.displayName || '—'}</td>
                <td>{row.cohortName || '—'}</td>
                <td><span className={`status-pill import-status-${row.status}`}>{STATUS_LABELS[row.status]}</span></td>
                <td className="import-note">{row.note || ''}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="modal-footer">
        <button type="button" onClick={onCancel} className="btn btn-secondary flex-1" disabled={loading}>
          Cancel
        </button>
        <button
          type="button"
          onClick={onConfirm}
          className="btn btn-primary flex-1"
          disabled={loading || selectedRowNumbers.size === 0}
        >
          {loading ? 'Creating users...' : `Create ${selectedRowNumbers.size} User${selectedRowNumbers.size === 1 ? '' : 's'}`}
        </button>
      </div>
    </div>
  );
};

const ResultsStep = ({ resultsData, onDone }) => {
  const { summary, created, createdWithoutCohort, skippedExisting, errors } = resultsData;

  return (
    <div>
      <div className="import-summary-strip mb-4">
        <span className="import-summary-item valid">{summary.createdCount} created</span>
        <span className="import-summary-item warning">{summary.createdWithoutCohortCount} without cohort</span>
        <span className="import-summary-item skipped">{summary.skippedExistingCount} skipped</span>
        <span className="import-summary-item error">{summary.errorCount} errors</span>
      </div>
      <p className="text-sm text-gray-600 mb-4">
        {summary.emailsSentCount} invitation email{summary.emailsSentCount === 1 ? '' : 's'} sent
        {summary.emailsFailedCount > 0 && `, ${summary.emailsFailedCount} failed to send (users were still created)`}.
      </p>

      {createdWithoutCohort.length > 0 && (
        <div className="import-results-section">
          <h4>Created without a cohort ({createdWithoutCohort.length})</h4>
          <ul className="import-results-list">
            {createdWithoutCohort.map(u => (
              <li key={u.id}>{u.email} — requested cohort &apos;{u.requestedCohortName}&apos;</li>
            ))}
          </ul>
        </div>
      )}

      {created.length > 0 && (
        <div className="import-results-section">
          <h4>Created ({created.length})</h4>
          <ul className="import-results-list">
            {created.map(u => (
              <li key={u.id}>{u.email}{u.cohort ? ` — ${u.cohort.name}` : ''}</li>
            ))}
          </ul>
        </div>
      )}

      {skippedExisting.length > 0 && (
        <div className="import-results-section">
          <h4>Skipped — already exists ({skippedExisting.length})</h4>
          <ul className="import-results-list">
            {skippedExisting.map((u, i) => (
              <li key={`${u.email}-${i}`}>{u.email} — {u.reason}</li>
            ))}
          </ul>
        </div>
      )}

      {errors.length > 0 && (
        <div className="import-results-section">
          <h4>Errors ({errors.length})</h4>
          <ul className="import-results-list">
            {errors.map((e, i) => (
              <li key={`${e.email}-${i}`}>{e.email || '(no email)'} — {e.reason}</li>
            ))}
          </ul>
        </div>
      )}

      <div className="modal-footer">
        <button type="button" onClick={onDone} className="btn btn-primary flex-1">
          Done
        </button>
      </div>
    </div>
  );
};

UploadStep.propTypes = {
  file: PropTypes.object,
  loading: PropTypes.bool.isRequired,
  onFileSelected: PropTypes.func.isRequired,
  onDownloadTemplate: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  onPreview: PropTypes.func.isRequired
};

PreviewStep.propTypes = {
  previewData: PropTypes.object.isRequired,
  selectedRowNumbers: PropTypes.instanceOf(Set).isRequired,
  loading: PropTypes.bool.isRequired,
  onToggleRow: PropTypes.func.isRequired,
  onToggleAll: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired
};

ResultsStep.propTypes = {
  resultsData: PropTypes.object.isRequired,
  onDone: PropTypes.func.isRequired
};

BulkUserImportModal.propTypes = {
  tenantId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  onClose: PropTypes.func.isRequired,
  onComplete: PropTypes.func.isRequired
};

export default BulkUserImportModal;
